import { i18n } from "@/i18n";
import { formManager } from "@/services/form/form-manager";
import { tabsManager } from "@/services/utilities/tabs-manager";

const EmailRegex =
  /[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*@(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?/;
const FloatRegex = /^[+-]?([0-9]*[.|,])?[0-9]+$/;
const PasswordRegex = /(?=.*\d)(?=.*[a-z])(?=.*[A-Z])(?=.*[^A-Za-z\d]).{12,}$/;

class FormValidator {
  validEmail(email) {
    return EmailRegex.test(email);
  }

  validNumberOrFloat(value) {
    return FloatRegex.test(value);
  }

  validMark(value) {
    return FloatRegex.test(value);
  }

  validPassword(value) {
    return PasswordRegex.test(value);
  }

  validCheckbox(content) {
    let valid = false;
    Object.values(content).forEach((value) => {
      if (value.choices) {
        Object.values(value.choices).forEach((choice) => {
          if (choice.checked && !valid) {
            valid = true;
          }
        });
      } else {
        if (value.checked && !valid) {
          valid = true;
        }
      }
    });
    return valid;
  }

  validFileExtension(filename, extensionsAccepted) {
    let valid = true;
    let type;
    const typeFromName = filename.match(/(.*)\.(.*)/).slice(-1)[0];
    if (typeFromName) {
      type = "." + filename.match(/(.*)\.(.*)/).slice(-1)[0];
    }
    if (extensionsAccepted && !new RegExp(extensionsAccepted).test(type.toLowerCase())) {
      valid = false;
    }
    return valid;
  }

  validFileSize(size, maxFileSize) {
    let valid = true;
    if (size > maxFileSize * 1024 * 1024) {
      valid = false;
    }
    return valid;
  }

  isVisible(model) {
    const el = document.getElementById(model.vars.id);
    if (el) {
      if (el.offsetHeight || el.getClientRects().length) {
        model.vars.ignoreValidation = false;
      }
      if (el.classList.contains("ignore-validation")) {
        model.vars.ignoreValidation = true;
      }
    }
  }

  validChildren(errors, model, $this) {
    Object.values(model.children).forEach((children) => {
      errors = errors.concat($this.validModel(children));
    });
    return errors;
  }

  validForm(models, object = false, multipleCheckBox = false) {
    const $this = this;
    let errors = [];
    Object.values(models).forEach(function (model) {
      if (
        model?.vars?.attr &&
        model.vars.attr["hasChildren"] &&
        model.vars.attr["hasChildren"] === true &&
        Object.keys(model?.children).length
      ) {
        errors = $this.validChildren(errors, model, $this);
      }
      if (model.vars.block_prefixes[1] === "collection" && model.vars.id !== "role_permissions") {
        Object.values(model.children).forEach((child) => {
          errors = errors.concat($this.validForm(child.children));
        });
      }
      $this.isVisible(model);
      if (!model.vars.disabled && !model.vars.ignoreValidation) {
        errors = errors.concat($this.validModel(model, "", multipleCheckBox));
      }
    });

    this.focusErrorInput(errors);
    if (object) {
      return {
        errors: errors,
        models: models,
      };
    }
    return errors;
  }

  handleRequiredErrors(model, errors = []) {
    if (!model.vars.required) return;

    const isEmpty = this.isEmpty(model.vars.value);

    model.vars.isInvalid = isEmpty;
    model.vars.error = isEmpty ? i18n.global.t("requiredField") : null;

    if (isEmpty) {
      errors.push(model.vars.id);
    }
    return isEmpty;
  }

  isEmpty(value) {
    if (value === null || value === undefined) return true;
    if (typeof value === "string") return value.trim().length === 0;
    if (Array.isArray(value)) return value.length === 0;
    if (typeof value === "object") return Object.keys(value).length === 0;
    return false;
  }

  validModel(model, language = "", multipleCheckBox = false) {
    const errorMessage = i18n.global.t("requiredField");
    let error = "";
    let errors = [];

    if (!model) {
      return errors;
    }

    let content = model.translatable ? model.vars.value[language] : model.vars.value;
    const isRequiredError = this.handleRequiredErrors(model, errors);
    if (isRequiredError) {
      return errors;
    }

    if (model.vars.row && model.vars.required && typeof model.vars.value === "object" && !multipleCheckBox) {
      const errorArray = this.validClusterRow(model, errorMessage);
      if (errorArray.length) {
        error = errorArray;
      } else {
        delete model.vars.error;
      }
    } else if (model.vars.value && model.vars.attr && model.vars.attr["regex"]) {
      const regex = new RegExp(model.vars.attr["regex"].slice(1, -1));
      const regexResult = regex.test(model.vars.value);
      if (!regexResult) {
        error = model.vars.attr["regex-error"];
      } else {
        delete model.vars.error;
      }
    } else if (model.vars.row && model.vars.required && typeof model.vars.value === "object" && multipleCheckBox) {
      if (
        model?.vars?.value &&
        Array.isArray(model?.vars?.value) &&
        !model?.vars?.value.some((element) => element !== "")
      ) {
        const errorArray = this.validClusterRow(model, errorMessage);
        if (errorArray.length) {
          error = errorArray;
        }
      }
    } else if (
      model.vars.required &&
      (formManager.isTextType(model) ||
        formManager.isTextareaType(model) ||
        formManager.isPhoneType(model) ||
        formManager.isEditorType(model)) &&
      (!content || !content.length)
    ) {
      error = errorMessage;
    } else if (
      model.vars.required &&
      formManager.isRadioType(model) &&
      ((!content && content !== 0) || !content.toString().length)
    ) {
      error = errorMessage;
    } else if (model.vars.required && formManager.isDateType(model) && (!content || (content && !content.length))) {
      error = errorMessage;
    } else if (formManager.isNumberType(model)) {
      error = this.handleNumberError(model);
    } else if (model.vars.required && formManager.isFileType(model) && (!content || (content && !("id" in content)))) {
      error = errorMessage;
    } else if (model.vars.required && formManager.isCheckboxType(model) && !this.validCheckbox(content)) {
      error = errorMessage;
    } else if (model.vars.required && formManager.isSelectType(model) && (!content || content === "")) {
      error = errorMessage;
    } else if (formManager.isMultiple(model) && formManager.isSelectType(model)) {
      if ((!content || (content && !Object.keys(content).length)) && model.vars.required) {
        error = errorMessage;
      } else if (
        model.vars.attr &&
        model.vars.attr["data-min-tag"] &&
        Object.keys(content).length &&
        Object.keys(content).length < model.vars.attr["data-min-tag"]
      ) {
        error = i18n.global.t("minTagError") + " (" + model.vars.attr["data-min-tag"] + ")";
      } else if (
        model.vars.attr &&
        model.vars.attr["data-max-tag"] &&
        Object.keys(content).length &&
        Object.keys(content).length > model.vars.attr["data-max-tag"]
      ) {
        error = i18n.global.t("maxTagError") + " (" + model.vars.attr["data-max-tag"] + ")";
      } else {
        delete model.vars.error;
      }
    } else if (formManager.isEmailType(model)) {
      if (model.vars.required && !content.length) {
        error = errorMessage;
      } else if (content && content.length && !this.validEmail(content)) {
        error = i18n.global.t("emailError");
      } else {
        delete model.vars.error;
      }
    } else if (formManager.isPasswordType(model)) {
      if (model.vars.required && !content.length) {
        error = errorMessage;
      } else if (content && content.length && model?.vars?.attr?.check && !this.validPassword(content)) {
        error = i18n.global.t("password_not_valid");
      } else {
        delete model.vars.error;
      }
    } else if (formManager.isPhoneType(model) && !this.validPhone(model)) {
      error = i18n.global.t("phoneError");
    } else {
      if (model.translatable && model.vars.error) {
        delete model.vars.error[language];
      } else {
        delete model.vars.error;
      }
    }

    let rowIsValid = true;
    if (model.vars.row && error && typeof error === "object" && error.length) {
      Object.values(error).forEach((item) => {
        if (rowIsValid && item !== null) {
          rowIsValid = false;
        }
      });
    }
    if (error) {
      if (model.translatable) {
        model["vars"]["error"][language] = error;
      } else {
        model["vars"]["error"] = error;
      }
      if (!model.vars.row || (model.vars.row && !rowIsValid)) {
        errors.push(model.vars.id);
      }
    }
    return errors;
  }

  validClusterRow(model, errorMessage) {
    let rowErrors = [];
    let index = 0;
    let answered = 0;
    Object.values(model.vars.value).forEach((value) => {
      if (!value || (value && !value.length)) {
        rowErrors[index] = errorMessage;
      } else {
        rowErrors[index] = null;
        answered++;
      }
      index++;
    });

    if (formManager.isRadioType(model) && answered) {
      rowErrors = [];
    }
    return rowErrors;
  }

  validPhone(model) {
    if (!model.vars.value) return true;
    const regex = /^\+?[0-9\s\-().]{7,}$/;
    return regex.test(model.vars.value);
  }

  validRepeatedPassword(password, repeatedPassword, errors) {
    if (password && repeatedPassword && password?.vars?.value !== repeatedPassword?.vars?.value) {
      repeatedPassword.vars.error = i18n.global.t("second_password_not_valid");
      errors.push(repeatedPassword.vars.id);
    }
    return errors;
  }

  processResponses(responses, models, returnAlerts = false) {
    let errors = [];
    let alerts = [];
    if (responses.length) {
      responses.forEach((error) => {
        const field = Object.values(models).find((item) => item.vars.name === error.field);
        if (field) {
          if (!error.context || (error.context && !error.context.alertId)) {
            field.vars.error = error.message;
            errors = errors.concat(field.vars.id);
            this.clearModelAlerts(field);
            field.vars.isInvalid = false;
          } else {
            const fieldAlert = field.vars.attr.alerts.find((alert) => alert.id === error.context.alertId);
            if (fieldAlert) {
              fieldAlert.isTriggered = true;
              fieldAlert.fieldName = field.vars.label;
              fieldAlert.fieldId = field.vars.id;
              field.vars.isInvalid = fieldAlert.level > 1;
              alerts.push(fieldAlert);
            } else {
              field.vars.isInvalid = false;
            }
          }
        }
      });
    }
    this.focusErrorInput(errors);
    if (returnAlerts) {
      return alerts;
    }
    return errors;
  }

  focusErrorInput(errors) {
    if (errors.length) {
      const invalidInput = document.getElementById(errors[0]);
      if (invalidInput) {
        const offset = window.innerWidth < 768 ? 300 : 250;
        window.scrollTo({
          behavior: "smooth",
          top: invalidInput.getBoundingClientRect().top - document.body.getBoundingClientRect().top - offset,
        });
      }
    }
  }

  validTabs(tabs) {
    let errors = [];
    let valid = false;
    let invalidTab = {};
    Object.values(tabs).forEach(function (tab) {
      if (!errors.length && !tab.hidden && !tab.disabled) {
        errors = errors.concat(formValidator.validForm(tab.models));
        if (errors.length) {
          invalidTab = tab;
        }
      }
    });

    if (Object.keys(invalidTab).length) {
      tabsManager.displayTab(invalidTab);
    } else {
      valid = true;
    }

    return valid;
  }

  handleNumberError(model) {
    if (model.vars.block_prefixes[1] !== "number") return;
    const vars = model.vars;
    if (!vars.value || vars.value === "" || vars.block_prefixes[1] !== "number") return;

    if (!this.validNumberOrFloat(vars.value)) {
      return this.handleError(i18n.global.t("requiredNumberOfFloat"), true, model);
    }

    const value = parseInt(vars.value);
    const { attr } = vars;

    if ((attr?.["data-number-min"] ?? null) !== null) {
      const min = parseInt(attr["data-number-min"]);
      const error = attr["data-error-min"] ?? i18n.global.t("form_error.minimum_value", { value: min });
      return this.handleError(value < min ? error : null, value < min, model);
    }

    if ((attr?.["data-number-max"] ?? null) !== null) {
      const max = parseInt(attr["data-number-max"]);
      const error = attr["data-error-max"] ?? i18n.global.t("form_error.maximum_value", { value: max });
      return this.handleError(value > max ? error : null, value > max, model);
    }
  }

  handleError(error, isInvalid, model) {
    model.vars.error = error;
    model.vars.isInvalid = isInvalid;
    return error;
  }

  clearModelAlerts(model) {
    model.vars.isInvalid = false;
    const clearedAlerts = [];
    if (model.vars?.attr?.alerts && Object.keys(model.vars.attr.alerts).length) {
      Object.values(model.vars?.attr?.alerts).forEach((alert) => {
        alert.isTriggered = false;
        alert.fieldName = model.vars.label;
        alert.fieldId = model.vars.id;
        clearedAlerts.push(alert);
      });
    }
    return clearedAlerts;
  }
}

export const formValidator = new FormValidator();
